import { isHidabrut } from './hidabrutPayload';

export const STORE_LINKS = {
  ios: 'https://apps.apple.com/app/id6757126849',
  android: 'https://play.google.com/store/apps/details?id=com.almog.bar_mitzva',
};

export type Platform = 'ios' | 'android' | 'desktop';

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** True when browser DevTools are open (docked). Detects the gap between
 *  outer and inner window dimensions that docked DevTools creates. */
export function isDevToolsOpen(): boolean {
  // @ts-ignore
  return !!window['__REACT_DEVTOOLS_GLOBAL_HOOK__'];
}

/** True when running inside the Kol Torah Flutter WebView (app already installed) — not Hidabrut. */
export function isInsideFlutterApp(): boolean {
  return (
    typeof (window as unknown as { flutter_inappwebview?: unknown }).flutter_inappwebview !==
      'undefined' && !isHidabrut()
  );
}

/** Redirect to the appropriate store, or do nothing on desktop. */
export function redirectToStore(): void {
  const platform = detectPlatform();
  if (platform === 'ios') window.location.href = STORE_LINKS.ios;
  else if (platform === 'android') window.location.href = STORE_LINKS.android;
}

type FlutterBridge = { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> };
type FlutterCapabilities = { openExternalUrl?: boolean };

/**
 * True when it's safe to hand a URL off to the native `openExternalUrl`
 * handler. Outside the Flutter app, `openExternalUrl` always falls back to
 * `window.open`, so it's safe. Inside the Flutter app it's only safe once the
 * native side has advertised support via `window.__flutterCapabilities` —
 * older installed app builds predate the `openExternalUrl` handler and would
 * otherwise fail silently when a JS caller relies on it (e.g. the Rate Us
 * button, which needs to escape the WebView to the app store).
 */
export function isExternalUrlSupported(): boolean {
  if (!isInsideFlutterApp()) return true;
  return (
    (window as unknown as { __flutterCapabilities?: FlutterCapabilities }).__flutterCapabilities
      ?.openExternalUrl === true
  );
}

/**
 * Opens a URL outside the app's embedded WebView (system browser or native
 * store app). A WebView cannot follow the store pages' redirects to
 * non-http(s) schemes (e.g. Play Store's `market://` links) on its own —
 * this asks the native Flutter shell to hand the URL to `url_launcher`
 * (external application mode) instead of navigating the WebView to it.
 *
 * Requires the native app to register a `openExternalUrl` JS handler, e.g.:
 *   controller.addJavaScriptHandler(
 *     handlerName: 'openExternalUrl',
 *     callback: (args) => launchUrl(Uri.parse(args[0]), mode: LaunchMode.externalApplication),
 *   );
 */
export async function openExternalUrl(url: string): Promise<void> {
  const bridge = (window as Window & { flutter_inappwebview?: FlutterBridge }).flutter_inappwebview;
  if (bridge) {
    await bridge.callHandler('openExternalUrl', url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
